// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  SquareRounded,
  TextureRounded,
} from '@/constants/hardfin-standard-icons';
import { useTimeline } from '@/contexts/timeline-context';
import { Box, Typography } from '@mui/material';

interface Props {
  /**
   * If true, the tooltip is visible.
   */
  isOpen: boolean;

  /**
   * An element to which to attach the tooltip.
   */
  anchorElement: Element | null;

  /**
   * A callback fired when the tooltip requests to be closed.
   */
  onClose: () => void;
}

export function TimelineLegendTooltip(props: Props) {
  const { palette } = useTimeline();

  return (
    <EducationalTooltip
      title="Timeline colors"
      isOpen={props.isOpen}
      onClose={props.onClose}
      anchorElement={props.anchorElement}
    >
      <Box
        display="grid"
        alignItems="center"
        gridTemplateColumns="auto 1fr"
        gridTemplateRows="auto auto"
        columnGap={1}
      >
        <SquareRounded fontSize="small" sx={{ fill: palette.asset.solid }} />
        <Typography variant="subtitle2">Movement</Typography>
        <Typography variant="body2" gridColumn="2 / 2" marginBottom={2}>
          Grey bars show asset movement from the start to the end of the move
        </Typography>
        <TextureRounded fontSize="small" sx={{ fill: palette.asset.solid }} />
        <Typography variant="subtitle2">Future movement</Typography>
        <Typography variant="body2" gridColumn="2 / 2" marginBottom={2}>
          Diagonal grey bars show <em>expected</em> asset movement from the
          start to the end of the <em>expected</em> move (if provided)
        </Typography>
        <SquareRounded fontSize="small" sx={{ fill: palette.project.solid }} />
        <Typography variant="subtitle2">Project</Typography>
        <Typography variant="body2" gridColumn="2 / 2" marginBottom={2}>
          Blue bars show the project duration from the project start date to the
          end date (or to the present if the project is still active)
        </Typography>
        <TextureRounded fontSize="small" sx={{ fill: palette.project.solid }} />
        <Typography variant="subtitle2">Project</Typography>
        <Typography variant="body2" gridColumn="2 / 2" marginBottom={2}>
          Diagonal blue bars show the <em>future</em> project duration through
          the project end date (from the present if the project is active, or
          from the start date if the project is upcoming)
        </Typography>
        <SquareRounded
          fontSize="small"
          sx={{ fill: palette.assignment.solid }}
        />
        <Typography variant="subtitle2">Project assignment</Typography>
        <Typography variant="body2" gridColumn="2 / 2" marginBottom={2}>
          Purple bars correspond to asset assignment from the assignment start
          date to the end date (or to the present if the assignment is still
          active)
        </Typography>
        <TextureRounded
          fontSize="small"
          sx={{ fill: palette.assignment.solid }}
        />
        <Typography variant="subtitle2">Project assignment</Typography>
        <Typography variant="body2" gridColumn="2 / 2">
          Diagonal purple bars correspond to <em>future</em> asset assignment
          (which does not end until the asset is unassigned)
        </Typography>
      </Box>
    </EducationalTooltip>
  );
}
